import { ArrowUpDown, ChevronDown, ChevronUp } from 'lucide-react';

export type SortField = 'date' | 'followers' | 'engagement' | 'price';
export type SortDirection = 'asc' | 'desc';

interface Column {
  key: string;
  label: string;
  sortable?: boolean;
  align?: 'left' | 'center' | 'right';
  width?: string;
}

interface CollaborationTableProps {
  collaborations: any[];
  onRowClick: (collaboration: any) => void;
  sortField: SortField | null;
  sortDirection: SortDirection;
  onSort: (field: SortField) => void;
}

const columns: Column[] = [
  { key: 'date', label: 'Date', sortable: true, width: 'w-24' },
  { key: 'creator', label: 'Créateur', sortable: false, width: 'w-48' },
  { key: 'followers', label: 'Followers', sortable: true, align: 'right', width: 'w-24' },
  { key: 'brand', label: 'Marque', sortable: false, width: 'w-32' },
  { key: 'platform', label: 'Plateforme', sortable: false, width: 'w-28' },
  { key: 'type', label: 'Type', sortable: false, width: 'w-24' },
  { key: 'engagement', label: 'Engagement', sortable: true, align: 'right', width: 'w-28' },
  { key: 'post_engagement', label: 'Eng. Post', sortable: false, align: 'right', width: 'w-24' },
  { key: 'price', label: 'Prix estimé', sortable: true, align: 'right', width: 'w-28' },
  { key: 'confidence', label: 'Score', sortable: false, align: 'center', width: 'w-24' },
];

export default function CollaborationTable({
  collaborations,
  onRowClick,
  sortField,
  sortDirection,
  onSort,
}: CollaborationTableProps) {
  const formatNumber = (num: number) => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(0)}K`;
    return num.toString();
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - date.getTime());
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return "Aujourd'hui";
    if (diffDays === 1) return 'Hier';
    if (diffDays < 7) return `${diffDays}j`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)}sem`;
    return `${Math.floor(diffDays / 30)}m`;
  };

  const getSortIcon = (field: SortField) => {
    if (sortField !== field) {
      return <ArrowUpDown className="w-3.5 h-3.5 text-white/30" />;
    }
    return sortDirection === 'asc' ? (
      <ChevronUp className="w-3.5 h-3.5 text-white/70" />
    ) : (
      <ChevronDown className="w-3.5 h-3.5 text-white/70" />
    );
  };

  const getConfidenceBadge = (score: string) => {
    const config = {
      high: { label: 'High', class: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30' },
      medium: { label: 'Medium', class: 'bg-amber-500/20 text-amber-400 border-amber-500/30' },
      low: { label: 'Low', class: 'bg-white/[0.06] text-white/40 border-white/[0.08]' },
    };
    const c = config[score as keyof typeof config] || config.medium;
    return (
      <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-semibold border ${c.class}`}>
        {c.label}
      </span>
    );
  };

  const getPlatformBadge = (platform: string) => {
    const config = {
      Instagram: { class: 'bg-pink-500/20 text-pink-400 border-pink-500/30' },
      TikTok: { class: 'bg-white/[0.08] text-white/70 border-white/[0.12]' },
      YouTube: { class: 'bg-red-500/20 text-red-400 border-red-500/30' },
    };
    const c = config[platform as keyof typeof config] || { class: 'bg-white/[0.06] text-white/50 border-white/[0.08]' };
    return (
      <span className={`inline-flex px-2 py-0.5 rounded-md text-xs font-medium border ${c.class}`}>
        {platform}
      </span>
    );
  };

  return (
    <div className="overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="border-b border-white/[0.06] sticky top-0 z-10 bg-white/[0.02]">
            <tr>
              {columns.map((col) => (
                <th
                  key={col.key}
                  className={`px-4 py-3 text-[10px] font-semibold text-white/30 uppercase tracking-widest ${
                    col.align === 'right' ? 'text-right' : col.align === 'center' ? 'text-center' : 'text-left'
                  } ${col.width || ''}`}
                >
                  {col.sortable ? (
                    <button
                      onClick={() => onSort(col.key as SortField)}
                      className="flex items-center gap-1.5 hover:text-white/60 transition-colors group w-full justify-between"
                    >
                      <span>{col.label}</span>
                      {getSortIcon(col.key as SortField)}
                    </button>
                  ) : (
                    col.label
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-white/[0.04]">
            {collaborations.map((collab) => (
              <tr
                key={collab.id}
                onClick={() => onRowClick(collab)}
                className="hover:bg-white/[0.04] cursor-pointer transition-colors"
              >
                <td className="px-4 py-3 text-sm text-white/50">
                  {formatDate(collab.date_detected)}
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2.5">
                    <img
                      src={collab.creator_avatar || 'https://via.placeholder.com/40'}
                      alt={collab.creator_name}
                      className="w-8 h-8 rounded-full object-cover border border-white/[0.08]"
                      onError={(e) => {
                        e.currentTarget.src = 'https://via.placeholder.com/40';
                      }}
                    />
                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-white truncate">
                        {collab.creator_name}
                      </div>
                      <div className="text-xs text-white/40 truncate">
                        @{collab.creator_handle}
                      </div>
                    </div>
                  </div>
                </td>
                <td className="px-4 py-3 text-sm font-medium text-white/80 text-right">
                  {formatNumber(collab.followers)}
                </td>
                <td className="px-4 py-3 text-sm text-white/80 font-medium">
                  {collab.brand_name}
                </td>
                <td className="px-4 py-3">
                  {getPlatformBadge(collab.platform)}
                </td>
                <td className="px-4 py-3 text-sm text-white/50">
                  {collab.content_type}
                </td>
                <td className="px-4 py-3 text-sm font-semibold text-right">
                  <div className="flex flex-col items-end gap-0.5">
                    <span className={`${
                      collab.engagement_rate > 3 ? 'text-emerald-400' :
                      collab.engagement_rate >= 1 ? 'text-amber-400' :
                      'text-white/50'
                    }`}>
                      {collab.engagement_rate.toFixed(2)}%
                    </span>
                    <span className="text-[10px] text-white/30 font-normal">moy. 15 posts</span>
                  </div>
                </td>
                <td className="px-4 py-3 text-sm font-semibold text-right">
                  <span className={`${
                    collab.post_engagement_rate > 3 ? 'text-emerald-400' :
                    collab.post_engagement_rate >= 1 ? 'text-amber-400' :
                    'text-white/50'
                  }`}>
                    {collab.post_engagement_rate.toFixed(2)}%
                  </span>
                </td>
                <td className="px-4 py-3 text-sm font-semibold text-white/80 text-right">
                  {collab.estimated_price_min}€
                </td>
                <td className="px-4 py-3 text-center">
                  {getConfidenceBadge(collab.confidence_score)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
